// Keep in sync with backend/src/stripe/plans.ts

export interface Plan {
  slug: string;
  name: string;
  tagline: string; // short value-prop shown next to plan name in billing UI
  priceInCents: number; // monthly, USD cents
  annualPriceInCents: number; // annual, USD cents (full year)
  messages: number;
  topUpRateMicros: number;
  // Optional: the backend org billing subscription plan payload omits `features` (same as `tagline`).
  // Local PLANS source always populates it; the optionality lives at the type boundary only.
  features?: string[];
  popular?: boolean;
}

export const PLANS: Plan[] = [
  {
    slug: 'free',
    name: 'Launch',
    tagline: 'Kick the tires',
    priceInCents: 0,
    annualPriceInCents: 0,
    messages: 2000,
    topUpRateMicros: 1000,
    features: [
      '2,000 messages/mo',
      '1 WhatsApp Business channel',
      'Sandbox access',
      'Inbound and outbound messaging',
      '24-hour webhook log retention',
      'CLI access',
      'Community support',
    ],
  },
  {
    slug: 'starter',
    name: 'Build',
    tagline: 'For solo vibecoders shipping their first integration',
    priceInCents: 1200,
    annualPriceInCents: 12000,
    messages: 30000,
    topUpRateMicros: 1000,
    features: [
      'Everything in Launch +',
      '30,000 messages/mo',
      'Production WhatsApp Business channel',
      'Unlimited webhook endpoints',
      '7-day webhook log retention',
      'Email support',
    ],
  },
  {
    slug: 'growth',
    name: 'Scale',
    tagline: 'For growing teams handling steady customer traffic',
    priceInCents: 2400,
    annualPriceInCents: 24000,
    messages: 100000,
    topUpRateMicros: 1000,
    features: [
      'Everything in Build +',
      '100,000 messages/mo',
      'Multiple workspace members',
      'Priority email support',
      'Advanced webhook retries',
    ],
    popular: true,
  },
  {
    slug: 'pro',
    name: 'Business',
    tagline: 'For established businesses with high message volume',
    priceInCents: 3900,
    annualPriceInCents: 39000,
    messages: 250000,
    topUpRateMicros: 1000,
    features: [
      'Everything in Scale +',
      '250,000 messages/mo',
      'Unlimited workspace members',
      'Dedicated Slack support',
      'Custom SLA',
    ],
  },
];

export function getPlanBySlug(slug: string): Plan | undefined {
  return PLANS.find((p) => p.slug === slug);
}

// Mirrors backend `nextPlanSlug` (packages/shared plans-catalog.ts): the upgrade
// ladder is free → starter → growth → pro. Returns undefined at the top tier OR
// for an off-ladder slug (e.g. a custom plan) — both cases fall through to
// per-message pay-as-you-go, so the UI treats them identically.
const PLAN_ORDER = ['free', 'starter', 'growth', 'pro'] as const;

export function nextPlan(slug: string): Plan | undefined {
  const i = PLAN_ORDER.indexOf(slug as (typeof PLAN_ORDER)[number]);
  if (i < 0 || i >= PLAN_ORDER.length - 1) return undefined;
  return getPlanBySlug(PLAN_ORDER[i + 1]);
}

// Position on the upgrade ladder (free=0 … pro=3). Off-ladder slugs (e.g.
// custom) return -1. Used to label a tier-switch CTA as Upgrade vs Downgrade
// relative to the org's current plan.
export function planRank(slug: string): number {
  return PLAN_ORDER.indexOf(slug as (typeof PLAN_ORDER)[number]);
}

// --- AIT-420 money model v2 -------------------------------------------------
// The v2 tiers are metered in ACTIONS, not messages. Numbers mirror
// `PLAN_CATALOG_V2` in @hookmyapp/shared — declared locally for the same reason
// PLANS above is (the shared package ships CJS, whose re-exported values Vite
// cannot resolve as ESM named imports). `__tests__/plans-v2.test.ts` locks
// these against the shared catalog so they can never drift.

export interface V2Plan {
  slug: 'build' | 'scale' | 'business';
  name: string;
  tagline: string;
  priceInCents: number;
  annualPriceInCents: number;
  actions: number;
  popular?: boolean;
}

export const V2_PLANS: V2Plan[] = [
  {
    slug: 'build',
    name: 'Build',
    tagline: 'For solo vibecoders shipping their first integration',
    priceInCents: 100,
    annualPriceInCents: 1000,
    actions: 200,
  },
  {
    slug: 'scale',
    name: 'Scale',
    tagline: 'For growing teams handling steady customer traffic',
    priceInCents: 2400,
    annualPriceInCents: 24000,
    actions: 15000,
    popular: true,
  },
  {
    slug: 'business',
    name: 'Business',
    tagline: 'For established businesses with high actions volume',
    priceInCents: 9700,
    annualPriceInCents: 97000,
    actions: 100000,
  },
];

export function getV2Plan(slug: string): V2Plan | undefined {
  return V2_PLANS.find((p) => p.slug === slug);
}

export function isV2PlanSlug(slug: string): slug is 'build' | 'scale' | 'business' {
  return slug === 'build' || slug === 'scale' || slug === 'business';
}

/** build -> scale -> business; business (and anything else) -> null. */
export function nextV2PlanSlug(slug: string): 'scale' | 'business' | null {
  if (slug === 'build') return 'scale';
  if (slug === 'scale') return 'business';
  return null;
}
